import type { FC } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import { formatPhoneNumber } from "@ui/utils/formatPhoneNumber";

import { useConfig } from "@jti/app-context/useConfig";
import { Button } from "@jti/core-components/components/Button";
import type { Address } from "@jti/fe-common";
import { formatFullName } from "@jti/fe-common";

import { AddressStreetInfo } from "./components";

import styles from "./AddressInfoBlock.module.scss";

export type AddressInfoBlockProps = {
  companyName?: string;
  address: Address;
  hideTelephone?: boolean;
  hidePostCode?: boolean;
  hideName?: boolean;
  editLabel?: string;
  onEdit?(): void;
  isEditable?: boolean;
  telephonePosition?: "top" | "bottom";
};

export const AddressInfoBlock: FC<AddressInfoBlockProps> = ({
  companyName,
  address,
  hideTelephone = false,
  hideName = false,
  hidePostCode = false,
  editLabel = "Edit",
  onEdit,
  isEditable = true,
  telephonePosition = "bottom",
}) => {
  const { __ } = useTranslations();
  const config = useConfig();

  const isNameVisible = !hideName && (address.firstname || address.lastname);

  const displayTelephoneOnTop = telephonePosition === "top" && address.telephone && !hideTelephone;
  const displayTelephoneOnBottom = telephonePosition === "bottom" && address.telephone && !hideTelephone;

  return (
    <>
      <div className={styles.container}>
        <div>
          {companyName && (
            <div className={styles.name} data-testid="AddressInfoCompanyName">
              {companyName}
            </div>
          )}
          {isNameVisible && (
            <div className={styles.name} data-testid="AddressInfoFullName">
              {formatFullName(address.firstname, address.lastname, config.isReversedNameOrder)}
            </div>
          )}
        </div>
        <div>
          {displayTelephoneOnTop && (
            <div data-testid="AddressInfoPhone">
              {formatPhoneNumber(address.telephone, config.isInternationalPhoneEnabled, config.PHONE_FORMAT)}
            </div>
          )}
          <AddressStreetInfo
            address={address}
            variant={config.addressFormVariant}
            hidePostCode={hidePostCode}
            isStreetFieldCombined={config.isStreetFieldCombined}
          />
          {displayTelephoneOnBottom && (
            <div data-testid="AddressInfoPhone">
              <span className={styles.phone}>{__("address_info.phone")}:</span>{" "}
              {formatPhoneNumber(address.telephone, config.isInternationalPhoneEnabled, config.PHONE_FORMAT)}
            </div>
          )}
        </div>
        {isEditable && onEdit ? (
          <Button variant="text" size="lg" onClick={onEdit}>
            {editLabel}
          </Button>
        ) : null}
      </div>
    </>
  );
};
